import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import TagInput from '../components/TagInput';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { NEWSLETTERS } from './MesNewsletters';

const API = 'https://aglomera.com/api';
const TOTAL_STEPS = 4;

interface LocalisationPref {
  label: string;
  distance_km: number;
}

/* ── Slider personnalisé ── */
const RangeSlider: React.FC<{
  min: number; max: number; step: number;
  value: number; onChange: (v: number) => void;
}> = ({ min, max, step, value, onChange }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative pt-1 pb-1">
      <style>{`
        .rng-ob { -webkit-appearance: none; appearance: none; width: 100%; height: 6px; border-radius: 9999px; outline: none; cursor: pointer; }
        .rng-ob::-webkit-slider-thumb { -webkit-appearance: none; width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 2.5px solid #2a52a8; box-shadow: 0 2px 10px rgba(42,82,168,0.22); cursor: pointer; transition: transform .15s; }
        .rng-ob::-webkit-slider-thumb:hover { transform: scale(1.18); }
        .rng-ob::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 2.5px solid #2a52a8; }
      `}</style>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        className="rng-ob"
        style={{ background: `linear-gradient(to right, #2a52a8 ${pct}%, #e5e7eb ${pct}%)` }}
      />
    </div>
  );
};

const pillCls = (selected: boolean) =>
  `px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer ${
    selected
      ? 'text-white border-transparent'
      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
  }`;

const CONTRATS = ['CDI', 'CDD', 'Stage', 'Alternance', 'VIE/Graduate', 'Freelance'];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1 — Types de contrat
  const [typesContrat, setTypesContrat] = useState<string[]>([]);

  // Step 2 — Postes visés
  const [postesVises, setPostesVises] = useState<string[]>([]);

  // Step 3 — Localisations (multiple, obligatoire ≥1)
  const [localisations, setLocalisations] = useState<LocalisationPref[]>([
    { label: '', distance_km: 40 },
  ]);

  // Step 4 — Newsletter (slug -> abonnementId)
  const [subscribedMap, setSubscribedMap] = useState<Record<string, number>>({});
  const [subscribingSlug, setSubscribingSlug] = useState<string | null>(null);
  const [plan, setPlan] = useState<'gratuit' | 'pro' | 'premium'>('gratuit');

  // Fetch plan from API
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/profil/`, { headers: await apiHeaders() });
        if (res.ok) {
          const d = await res.json();
          if (d.plan) setPlan(d.plan);
        }
      } catch {}
    })();
  }, []);

  const maxNewsletters = plan === 'premium' ? 3 : 1;

  const toggleContrat = (c: string) => {
    setTypesContrat(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  /* ── Localisation helpers ── */
  const updateLocLabel = (idx: number, v: string) =>
    setLocalisations(prev => prev.map((l, i) => i === idx ? { ...l, label: v } : l));

  const updateLocDistance = (idx: number, v: number) =>
    setLocalisations(prev => prev.map((l, i) => i === idx ? { ...l, distance_km: v } : l));

  const addLoc = () =>
    setLocalisations(prev => [...prev, { label: '', distance_km: 40 }]);

  const removeLoc = (idx: number) =>
    setLocalisations(prev => prev.filter((_, i) => i !== idx));

  /* ── Newsletter helpers ── */
  const handleToggleNewsletter = async (slug: string) => {
    const user = auth.currentUser;
    if (!user || subscribingSlug) return;

    const existingId = subscribedMap[slug];

    if (existingId) {
      // Désabonnement
      setSubscribingSlug(slug);
      try {
        const res = await fetch(`${API}/newsletters/abonnements/${existingId}/`, {
          method: 'DELETE',
          headers: await apiHeaders(),
        });
        if (res.ok || res.status === 204) {
          setSubscribedMap(prev => {
            const next = { ...prev };
            delete next[slug];
            return next;
          });
        }
      } catch {}
      setSubscribingSlug(null);
    } else {
      // Abonnement
      const quotaAtteint = Object.keys(subscribedMap).length >= maxNewsletters;
      if (quotaAtteint) return;
      setSubscribingSlug(slug);
      try {
        const res = await fetch(`${API}/newsletters/abonnements/`, {
          method: 'POST',
          headers: await apiHeaders(),
          body: JSON.stringify({ newsletter_slug: slug, localisation: '', types_contrat: [] }),
        });
        if (res.ok) {
          const data = await res.json();
          setSubscribedMap(prev => ({ ...prev, [slug]: data.id }));
        }
      } catch {}
      setSubscribingSlug(null);
    }
  };

  /* ── Finish ── */
  const handleFinish = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setSaving(true);
    try {
      const locPayload = localisations
        .filter(l => l.label.trim())
        .map(l => ({ label: l.label.trim(), distance_km: l.distance_km }));
      const mappedContrats = typesContrat.map(c => c === 'VIE/Graduate' ? 'VIE / Graduate' : c);

      await fetch(`${API}/profil/`, {
        method: 'PATCH',
        headers: await apiHeaders(),
        body: JSON.stringify({
          types_contrat_recherches: mappedContrats,
          postes_vises:             postesVises,
          localisations:            locPayload,
          frequence_email:          'quotidienne',
        }),
      });
    } catch {
      // on redirige quand même
    } finally {
      setSaving(false);
      navigate('/alertes', { replace: true });
    }
  };

  const canNext = () => {
    if (step === 1) return typesContrat.length > 0;
    if (step === 2) return postesVises.length > 0;
    if (step === 3) return localisations[0].label.trim() !== '';
    return Object.keys(subscribedMap).length > 0;
  };

  const subscribedCount = Object.keys(subscribedMap).length;
  const quotaAtteint = subscribedCount >= maxNewsletters;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: '#fafaf8' }}>

      {/* Card */}
      <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full transition-all duration-300"
            style={{ background: '#2a52a8', width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        <div className="p-6">
          {/* Header */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Étape {step} sur {TOTAL_STEPS}
            </p>
            <h1 className="text-xl font-bold text-gray-900">
              {step === 1 && 'Quels types de contrat recherchez-vous ?'}
              {step === 2 && 'Quels postes visez-vous ?'}
              {step === 3 && 'Où souhaitez-vous travailler ?'}
              {step === 4 && 'Choisissez votre newsletter'}
            </h1>
          </div>

          {/* ── Step 1 : contrats ── */}
          {step === 1 && (
            <div className="flex flex-wrap gap-3 justify-center">
              {CONTRATS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleContrat(c)}
                  className={pillCls(typesContrat.includes(c))}
                  style={typesContrat.includes(c) ? { background: '#2a52a8' } : {}}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* ── Step 2 : postes visés ── */}
          {step === 2 && (
            <div>
              <p className="text-sm text-gray-500 mb-3">Appuyez sur Entrée pour valider chaque intitulé</p>
              <TagInput
                values={postesVises}
                onChange={setPostesVises}
                placeholder="Private Equity, Trader, Banquier privé(e)"
                tagBg="#e6edfa"
                tagColor="#2a52a8"
              />
            </div>
          )}

          {/* ── Step 3 : localisations ── */}
          {step === 3 && (
            <div className="space-y-4">
              {localisations.map((loc, idx) => (
                <div key={idx}>
                  {idx > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 h-px bg-gray-100" />
                      <span className="text-xs text-gray-400 font-medium">Ville {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeLoc(idx)}
                        className="text-xs text-gray-300 hover:text-red-400 transition-colors leading-none"
                        title="Supprimer cette ville"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <div className="space-y-3">
                    <LocationAutocomplete
                      value={loc.label}
                      onChange={v => updateLocLabel(idx, v)}
                      placeholder="Ville ou commune..."
                    />
                    {loc.label && (
                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-2">
                          <span>Rayon max.</span>
                          <span className="font-semibold" style={{ color: '#2a52a8' }}>{loc.distance_km} km</span>
                        </div>
                        <RangeSlider
                          min={5} max={150} step={5}
                          value={loc.distance_km}
                          onChange={v => updateLocDistance(idx, v)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {localisations[localisations.length - 1].label.trim() && (
                <button
                  type="button"
                  onClick={addLoc}
                  className="flex items-center gap-1.5 text-sm font-semibold transition-colors hover:opacity-80"
                  style={{ color: '#2a52a8' }}
                >
                  <span className="text-base leading-none">+</span>
                  <span>Ajouter une ville</span>
                </button>
              )}
              <p className="text-xs text-gray-400">Vous pourrez affiner cette préférence dans votre profil.</p>
            </div>
          )}

          {/* ── Step 4 : newsletters ── */}
          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 -mt-2 mb-1">
                {plan === 'premium'
                  ? 'Sélectionnez jusqu\'à 3 newsletters.'
                  : 'Sélectionnez 1 newsletter. Passez Premium pour en choisir plusieurs.'}
              </p>
              {NEWSLETTERS.map(nl => {
                const isSoon = nl.slug === 'gestion-de-patrimoine';
                const isSubscribed = !!subscribedMap[nl.slug];
                const isLoading = subscribingSlug === nl.slug;
                const isBlocked = !isSubscribed && quotaAtteint;
                return (
                  <div
                    key={nl.slug}
                    onClick={() => !isSoon && !isBlocked && !isLoading && handleToggleNewsletter(nl.slug)}
                    className="relative rounded-2xl overflow-hidden transition-all"
                    style={{
                      border: isSubscribed ? `2px solid ${nl.couleur}` : '2px solid #e5e7eb',
                      opacity: isSoon ? 0.65 : isBlocked ? 0.5 : 1,
                      cursor: isSoon ? 'not-allowed' : isBlocked ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {/* Image header */}
                    <div className="relative overflow-hidden" style={{ height: 72 }}>
                      <img
                        src={nl.image}
                        alt={nl.nom}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0" style={{ background: `${nl.couleur}cc` }} />
                      {isSoon && (
                        <div style={{ position: 'absolute', top: 0, right: 0, width: 88, height: 88, overflow: 'hidden', pointerEvents: 'none', zIndex: 10 }}>
                          <div style={{ position: 'absolute', top: 18, right: -38, width: 144, background: nl.couleur, color: 'white', fontSize: 9, fontWeight: 700, textAlign: 'center', padding: '4px 0', transform: 'rotate(45deg)', letterSpacing: '0.05em', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>
                            Bientôt
                          </div>
                        </div>
                      )}
                      <div className="relative z-10 px-4 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-white">{nl.nom}</p>
                          <p className="text-xs text-white opacity-80">{nl.tagline}</p>
                        </div>
                        {isLoading ? (
                          <div
                            className="w-5 h-5 rounded-full border-2 animate-spin flex-shrink-0"
                            style={{ borderColor: 'rgba(255,255,255,0.6)', borderTopColor: 'white' }}
                          />
                        ) : isSubscribed ? (
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'white' }}
                          >
                            <span style={{ color: nl.couleur, fontSize: 14, fontWeight: 700 }}>✓</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {/* Keywords */}
                    <div className="px-4 py-2 flex flex-wrap gap-1 justify-center" style={{ background: nl.couleurLight }}>
                      {nl.keywords.slice(0, 4).map(kw => (
                        <span
                          key={kw}
                          className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ background: nl.couleur, color: 'white' }}
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {quotaAtteint && plan !== 'premium' && (
                <p className="text-xs text-center" style={{ color: '#e65100' }}>
                  ⚡ Passez au plan Premium pour sélectionner plusieurs newsletters.
                </p>
              )}
            </div>
          )}

          {/* ── Navigation ── */}
          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-6 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-0"
            >
              Retour
            </button>

            {step < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
                style={{ background: '#2a52a8' }}
              >
                Suivant
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinish}
                disabled={saving || !canNext()}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
                style={{ background: '#2a52a8' }}
              >
                {saving ? 'Enregistrement...' : 'Terminer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
